import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserRound, MapPin, Timer, Target, Bookmark, BadgeCheck, Mail, Footprints, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authApi } from "@/context/AuthContext";
import { api, setPageMeta } from "@/lib/api";
import { ClubRow } from "@/components/ClubRow";
import { RunnerCardShare } from "@/components/RunnerCardShare";
import { Reveal } from "@/components/Motion";

const GOALS = ["5K", "10K", "HALF MARATHON", "MARATHON", "ULTRA", "JUST CONSISTENCY"];
const PACE_RE = /^\d{1,2}:[0-5]\d$/;
const inputCls = "w-full bg-transparent border border-white/20 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-white outline-none transition-colors duration-200";

const dayLabel = (iso) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${iso}T00:00:00`);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "TODAY";
  if (diff === 1) return "TOMORROW";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
};

export default function Profile() {
  const { user, loading, signIn, setUser } = useAuth();
  const [clubs, setClubs] = useState(null);
  const [feed, setFeed] = useState(null);
  const [miles, setMiles] = useState(null);
  const [showAllRuns, setShowAllRuns] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ city: "", pace: "", goal: "", digest_opt_in: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPageMeta("My Profile | Ninja Run Club", "Your runner profile — home city, pace, goal race, and saved clubs.");
  }, []);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm({ city: user.city || "", pace: user.pace || "", goal: user.goal || "", digest_opt_in: Boolean(user.digest_opt_in) });
    authApi.get("/me/saved-clubs").then((r) => setClubs(r.data.clubs)).catch(() => setClubs([]));
    authApi.get("/me/club-feed").then((r) => setFeed(r.data.items)).catch(() => setFeed([]));
    authApi.get("/me/checkins").then((r) => setMiles(r.data)).catch(() => setMiles(null));
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    const pace = form.pace.trim();
    if (pace && !PACE_RE.test(pace)) {
      toast.error("Pace should look like 9:30 — minutes:seconds per mile.");
      return;
    }
    setSaving(true);
    try {
      const r = await authApi.patch("/me/profile", { city: form.city, pace, goal: form.goal, digest_opt_in: form.digest_opt_in });
      setUser(r.data);
      toast.success("Profile saved. See you on the road.");
    } catch {
      toast.error("Could not save your profile. Try again.");
    }
    setSaving(false);
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await authApi.post("/me/digest/send-now");
      if (r.data.ok) toast.success("Digest sent. Check your inbox.");
      else toast("Nothing to send yet — save a club with meet days first.");
    } catch {
      toast.error("Could not send the digest. Try again.");
    }
    setSendingDigest(false);
  };

  const deleteCheckin = async (id) => {
    try {
      await authApi.delete(`/me/checkins/${id}`);
      const r = await authApi.get("/me/checkins");
      setMiles(r.data);
      toast.success("Run removed from your record.");
    } catch {
      toast.error("Could not remove that run.");
    }
  };

  if (loading) {
    return <p className="font-mono-label text-white/40 py-32 text-center">LOADING...</p>;
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center" data-testid="profile-signed-out">
        <UserRound size={28} className="text-white/30" />
        <h1 className="font-display text-4xl sm:text-5xl mt-6">YOUR RUNNER CARD LIVES HERE.</h1>
        <p className="text-white/50 max-w-md mt-4">Sign in to set your city, pace, and goal race — and keep every club you want to run with in one place.</p>
        <button
          data-testid="profile-signin"
          onClick={signIn}
          className="btn-invert mt-8 bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white"
        >
          SIGN IN WITH GOOGLE
        </button>
      </div>
    );
  }

  const memberSince = user.created_at ? new Date(user.created_at).getFullYear() : null;
  const stats = [
    { icon: MapPin, label: "CITY", value: user.city || "—" },
    { icon: Timer, label: "PACE", value: user.pace ? `${user.pace} /MI` : "—" },
    { icon: Target, label: "GOAL", value: user.goal || "—" },
    { icon: Bookmark, label: "SAVED", value: clubs ? String(clubs.length) : "—" },
  ];

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-14 pb-10 border-b border-white/10 bg-ink">
        <Reveal>
          <div className="flex items-center gap-5">
            {user.picture ? (
              <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="h-16 w-16 rounded-full border border-white/30" />
            ) : (
              <span className="h-16 w-16 rounded-full border border-white/30 flex items-center justify-center font-display text-2xl">{(user.name || "?")[0]}</span>
            )}
            <div>
              <p className="font-mono-label text-white/50">RUNNER CARD{memberSince ? ` · MEMBER SINCE ${memberSince}` : ""}</p>
              <h1 data-testid="profile-title" className="font-display text-4xl sm:text-5xl mt-2">{(user.name || "RUNNER").toUpperCase()}</h1>
              <p className="text-white/40 text-sm mt-1">{user.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 max-w-3xl" data-testid="profile-stats">
            {stats.map((s) => (
              <div key={s.label} className="border border-white/10 px-4 py-3.5">
                <p className="font-mono-label text-white/40 flex items-center gap-1.5"><s.icon size={12} /> {s.label}</p>
                <p className="font-display text-lg mt-1.5 truncate">{s.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <RunnerCardShare user={user} savedCount={clubs ? clubs.length : 0} />
          </div>
        </Reveal>
      </header>

      {feed && feed.length > 0 && (
        <section className="px-5 lg:px-10 pt-10" data-testid="profile-club-feed">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono-label text-white/40">THIS WEEK WITH YOUR CLUBS</p>
            <button
              type="button"
              data-testid="digest-send-now"
              onClick={sendDigestNow}
              disabled={sendingDigest}
              className="font-mono-label flex items-center gap-2 border border-white/25 px-4 py-2.5 text-white/60 hover:border-white hover:text-white transition-colors duration-200 disabled:opacity-50"
            >
              <Mail size={13} /> {sendingDigest ? "SENDING..." : "EMAIL ME THIS WEEK"}
            </button>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {feed.map((it, i) => (
              <div key={i} className="border border-white/10 px-4 py-4" data-testid={`feed-item-${i}`}>
                <p className="font-mono-label text-white/40">
                  {dayLabel(it.date)} · {it.type === "event" ? "EVENT" : "CLUB RUN"}
                </p>
                {it.type === "event" ? (
                  <>
                    <Link to="/events" className="font-display text-lg leading-tight mt-2 block hover:text-white/80 transition-colors duration-150">
                      {it.event.title.toUpperCase()}
                    </Link>
                    <p className="text-white/50 text-xs mt-1.5">
                      {it.event.time} {it.event.timezone} · {it.event.location}
                    </p>
                  </>
                ) : (
                  <>
                    <Link to={`/clubs/${it.club.slug}`} className="font-display text-lg leading-tight mt-2 flex items-center gap-1.5 hover:text-white/80 transition-colors duration-150">
                      <span className="truncate">{it.club.name.toUpperCase()}</span>
                      {it.club.verified && <BadgeCheck size={14} className="shrink-0 text-white/50" />}
                    </Link>
                    {it.note && <p className="text-white/50 text-xs mt-1.5 line-clamp-2">{it.note}</p>}
                  </>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {miles && (
        <section className="px-5 lg:px-10 pt-10" data-testid="profile-miles">
          <p className="font-mono-label text-white/40 flex items-center gap-2"><Footprints size={13} /> MY MILES</p>
          {miles.total_runs === 0 ? (
            <p className="text-white/40 text-sm mt-4 border border-dashed border-white/15 px-5 py-4" data-testid="profile-miles-empty">
              No runs logged yet. Hit "I RAN WITH THIS CLUB" on any club page after a run to build your history.
            </p>
          ) : (
            <div className="mt-5 grid lg:grid-cols-[280px_1fr] gap-8">
              <div className="space-y-3">
                <div className="border border-white/10 px-4 py-3.5">
                  <p className="font-mono-label text-white/40">TOTAL RUNS</p>
                  <p data-testid="profile-total-runs" className="font-display text-3xl mt-1">{miles.total_runs}</p>
                </div>
                <div className="border border-white/10 px-4 py-3.5">
                  <p className="font-mono-label text-white/40">TOTAL MILES</p>
                  <p data-testid="profile-total-miles" className="font-display text-3xl mt-1">{miles.total_miles}</p>
                </div>
                {miles.by_club[0] && (
                  <div className="border border-white/10 px-4 py-3.5">
                    <p className="font-mono-label text-white/40">TOP CLUB</p>
                    <p className="font-display text-lg mt-1 truncate">{miles.by_club[0].club_name.toUpperCase()}</p>
                    <p className="font-mono-label text-white/40 mt-0.5">{miles.by_club[0].runs} {miles.by_club[0].runs === 1 ? "RUN" : "RUNS"}</p>
                  </div>
                )}
              </div>
              <div data-testid="profile-checkins-list">
                {(showAllRuns ? miles.items : miles.items.slice(0, 10)).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 py-3 border-b border-white/5" data-testid={`checkin-row-${c.id}`}>
                    <div className="min-w-0">
                      <Link to={`/clubs/${c.club_slug}`} className="font-display text-base truncate block hover:text-white/80 transition-colors duration-150">
                        {c.club_name.toUpperCase()}
                      </Link>
                      <p className="font-mono-label text-white/40 mt-0.5">
                        {new Date(`${c.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase()}
                        {c.miles ? ` · ${c.miles} MI` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid={`checkin-delete-${c.id}`}
                      onClick={() => deleteCheckin(c.id)}
                      title="Remove this run"
                      className="p-1.5 text-white/30 hover:text-white transition-colors duration-150 shrink-0"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {miles.items.length > 10 && !showAllRuns && (
                  <button
                    type="button"
                    data-testid="profile-show-all-runs"
                    onClick={() => setShowAllRuns(true)}
                    className="font-mono-label mt-4 text-white/40 hover:text-white transition-colors duration-200"
                  >
                    SHOW ALL {miles.items.length} RUNS →
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      <div className="px-5 lg:px-10 py-10 grid lg:grid-cols-[400px_1fr] gap-10">
        <section>
          <p className="font-mono-label text-white/40">EDIT PROFILE</p>
          <form onSubmit={save} className="mt-5 space-y-5" data-testid="profile-form">
            <div>
              <label htmlFor="profile-city" className="font-mono-label text-white/60 block mb-2">HOME CITY</label>
              <input
                id="profile-city"
                data-testid="profile-city-input"
                type="text"
                list="profile-city-options"
                placeholder="DALLAS, TX"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className={inputCls}
              />
              <datalist id="profile-city-options">
                {cities.map((c) => <option key={c.slug} value={c.name} />)}
              </datalist>
            </div>
            <div>
              <label htmlFor="profile-pace" className="font-mono-label text-white/60 block mb-2">EASY PACE (MIN:SEC PER MILE)</label>
              <input
                id="profile-pace"
                data-testid="profile-pace-input"
                type="text"
                inputMode="numeric"
                placeholder="9:30"
                value={form.pace}
                onChange={(e) => setForm({ ...form, pace: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="profile-goal" className="font-mono-label text-white/60 block mb-2">CHASING</label>
              <select
                id="profile-goal"
                data-testid="profile-goal-select"
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                className={`${inputCls} cursor-pointer`}
              >
                <option value="" className="bg-night">PICK A GOAL</option>
                {GOALS.map((g) => <option key={g} value={g} className="bg-night">{g}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-3 cursor-pointer border border-white/10 px-4 py-3.5" data-testid="digest-toggle">
              <input
                type="checkbox"
                data-testid="digest-checkbox"
                checked={form.digest_opt_in}
                onChange={(e) => setForm({ ...form, digest_opt_in: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-white cursor-pointer"
              />
              <span>
                <span className="font-mono-label text-white/70 block">WEEKLY CLUB DIGEST</span>
                <span className="text-white/40 text-xs block mt-1">One email every Sunday with your clubs' runs and events for the week. Never miss a meet.</span>
              </span>
            </label>
            <button
              type="submit"
              data-testid="profile-save-btn"
              disabled={saving}
              className="btn-invert w-full bg-white text-ink font-mono-label px-6 py-4 border border-white hover:bg-transparent hover:text-white disabled:opacity-50"
            >
              {saving ? "SAVING..." : "SAVE PROFILE"}
            </button>
          </form>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <p className="font-mono-label text-white/40">MY CLUBS · {clubs ? clubs.length : "—"} SAVED</p>
            <Link to="/clubs" data-testid="profile-browse-clubs" className="font-mono-label text-white/50 hover:text-white link-underline transition-colors duration-200">
              BROWSE THE DIRECTORY →
            </Link>
          </div>
          <div className="mt-5">
            {clubs === null ? (
              <p className="font-mono-label text-white/40 py-14 text-center">LOADING YOUR CLUBS...</p>
            ) : clubs.length === 0 ? (
              <div className="border border-white/10 py-14 text-center" data-testid="profile-clubs-empty">
                <p className="font-display text-2xl">NOTHING SAVED YET.</p>
                <p className="text-white/50 mt-3 text-sm">Hit the bookmark on any club to build your shortlist.</p>
              </div>
            ) : (
              <div data-testid="profile-clubs-list">
                {clubs.map((c, i) => (
                  <ClubRow key={c.slug} club={c} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
